import { useTranslation } from "react-i18next";

import { useProfile } from "@/entities/profile";
import { useUpdateProfile } from "@/features/profile-settings";
import { Icon } from "@/shared/ui";
import { ScreenSkeleton, ScreenState } from "@/widgets/screen-state";
import { TopBar } from "@/widgets/top-bar";

/**
 * Язык. Figma: `Profile · Language` (26:171).
 *
 * Язык в продукте один. Английской локали нет и не планируется: бэкенд отдаёт
 * готовые русские строки — заголовки событий, комментарии к операциям, тексты
 * правовых документов, — и переводить их на клиенте нечем. Строка EN
 * нарисована в макете, поэтому остаётся на экране, но выключена (DESIGN.md §10).
 */
export function ProfileLanguagePage() {
  const { t } = useTranslation();
  const { data, isPending, isError, refetch } = useProfile();
  const update = useUpdateProfile();

  if (isPending) return <ScreenSkeleton blocks={[72, 72]} />;

  if (isError) {
    return (
      <ScreenState
        tone="loss"
        icon="warningFill"
        title={t("states.error.title")}
        body={t("states.error.body")}
        action={{ label: t("states.error.action"), onClick: () => void refetch() }}
      />
    );
  }

  const current = data.user.language === "en" ? "en" : "ru";

  return (
    <div className="flex flex-col gap-3 px-5 py-3">
      <TopBar title={t("profile.menu.language")} subtitle={t("profile.language.subtitle")} />

      <button
        type="button"
        aria-pressed={current === "ru"}
        data-haptic="select"
        onClick={() => update.mutate({ language: "ru" })}
        className={[
          "press-row flex items-center justify-between rounded-2xl border p-4 text-left",
          "transition-colors duration-[var(--motion-fast)] ease-standard",
          current === "ru"
            ? "border-action-primary bg-surface-selected"
            : "border-border-subtle bg-background-elevated",
        ].join(" ")}
      >
        <span className="text-label font-semibold text-text-primary">{t("profile.language.ru")}</span>
        {current === "ru" ? (
          <Icon name="check" size={16} className="text-action-primary" />
        ) : null}
      </button>

      <button
        type="button"
        disabled
        aria-disabled="true"
        className="flex items-center justify-between rounded-2xl border border-disabled-border bg-disabled-surface p-4 text-left"
      >
        <span className="text-label font-semibold text-disabled-text">{t("profile.language.en")}</span>
        <span className="text-caption font-normal text-disabled-text">
          {t("profile.language.enSoon")}
        </span>
      </button>
    </div>
  );
}
